import React from 'react';
import { RiMedalLine, RiAwardLine, RiPlaneLine } from '@remixicon/react';
import TrustScore from './TrustScore';
import { getOverallStatus, STATUS_STYLES } from '../utils/thresholds';

/**
 * Hero card at the top of the TrustScore Details page. Composes the
 * existing <TrustScore> circle with the operator's fleet count +
 * ARGUS / WYVERN badges and a short risk narrative sentence.
 *
 * Card background is tinted by the overall status — matches the
 * design's green-wash for a Premier score, and would flip to amber/red
 * for lower scores.
 */
const TrustScoreHero = ({ operator, overallScore, tier, narrative, fleetsCount }) => {
  const status = getOverallStatus(overallScore);
  const style = STATUS_STYLES[status] || STATUS_STYLES.acceptable;
  const numericScore = Number.isFinite(Number(overallScore)) ? Number(overallScore) : null;

  const argusRating = operator?.argus_rating && operator.argus_rating !== 'No' ? operator.argus_rating : null;
  const wyvernRating = operator?.wyvern_rating && operator.wyvern_rating !== 'No' ? operator.wyvern_rating : null;

  return (
    // Pill-shaped hero card — fully rounded corners match the design.
    // Interior padding is bumped a touch so the larger score circle and
    // its label ring don't touch the pill edge on the left side.
    <div className={`rounded-full border ${style.cardBorder} ${style.cardBg} py-2 md:py-3 px-1 md:px-3`}>
      <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
        <div className="flex-shrink-0">
          <TrustScore
            numericScore={numericScore}
            score={tier || 'Standard'}
            label={tier || 'Trust'}
            size="md-lg"
            strokeWidth={12}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
            TrustScore
          </p>
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            {fleetsCount != null && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-black/5 text-gray-700 text-xs rounded-full font-medium">
                <RiPlaneLine className="w-3.5 h-3.5" />
                {fleetsCount} FLEETS
              </span>
            )}
            {argusRating && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-black/5 text-gray-700 text-xs rounded-full font-medium uppercase">
                <RiMedalLine className="w-3.5 h-3.5" />
                {argusRating}
              </span>
            )}
            {wyvernRating && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-black/5 text-gray-700 text-xs rounded-full font-medium">
                <RiAwardLine className="w-3.5 h-3.5" />
                WYVERN WINGMAN
              </span>
            )}
          </div>
          {narrative && (
            <p className="text-sm text-gray-700 leading-relaxed">{narrative}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrustScoreHero;
